import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Loader2, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = { name: '', role: '', photo_url: '', order: 0 };

export default function ManageIntervenants() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/intervenants/all`, { headers });
      setItems(res.data);
    } catch (e) { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSave = async () => {
    if (!form.name || !form.role) { toast.error('Nom et fonction requis'); return; }
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/intervenants/${editId}`, form, { headers });
        toast.success('Intervenant mis à jour');
      } else if (file) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('name', form.name);
        fd.append('role', form.role);
        fd.append('order', form.order);
        await axios.post(`${API}/intervenants/upload`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
        toast.success('Intervenant ajouté');
      } else if (form.photo_url) {
        await axios.post(`${API}/intervenants`, form, { headers });
        toast.success('Intervenant ajouté');
      } else {
        toast.error('Photo requise'); setSaving(false); return;
      }
      setDialogOpen(false);
      setForm(emptyForm);
      setFile(null);
      setEditId(null);
      fetchItems();
    } catch (e) { toast.error('Erreur lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleEdit = (item) => {
    setForm({ name: item.name, role: item.role, photo_url: item.photo_url, order: item.order || 0 });
    setEditId(item.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet intervenant ?')) return;
    try {
      await axios.delete(`${API}/intervenants/${id}`, { headers });
      toast.success('Supprimé');
      fetchItems();
    } catch (e) { toast.error('Erreur'); }
  };

  const openAdd = () => { setForm(emptyForm); setEditId(null); setFile(null); setDialogOpen(true); };

  const imgSrc = (url) => url?.startsWith('/api') ? `${BACKEND_URL}${url}` : url;

  return (
    <div data-testid="manage-intervenants">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3A5F]">Intervenants</h1>
          <p className="text-sm text-[#6B7C93]">{items.length} intervenant(s)</p>
        </div>
        <Button onClick={openAdd} className="bg-[#1E3A5F] hover:bg-[#152A45]" data-testid="add-intervenant-btn">
          <Plus className="w-4 h-4 mr-2" />Ajouter
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Photo</TableHead>
                <TableHead>Nom</TableHead>
                <TableHead>Fonction</TableHead>
                <TableHead className="w-20">Ordre</TableHead>
                <TableHead className="w-20">Statut</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <img src={imgSrc(item.photo_url)} alt="" className="w-10 h-10 rounded-full object-cover" />
                  </TableCell>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell className="text-[#6B7C93]">{item.role}</TableCell>
                  <TableCell>{item.order}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded-full ${item.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {item.is_active !== false ? 'Actif' : 'Inactif'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} data-testid={`edit-intervenant-${item.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-intervenant-${item.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!loading && items.length === 0 && (
            <div className="text-center py-12 text-[#6B7C93]">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Aucun intervenant</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>{editId ? 'Modifier' : 'Ajouter'} un intervenant</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="intervenant-name-input" /></div>
            <div><Label>Fonction *</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="intervenant-role-input" /></div>
            {!editId && (
              <div><Label>Photo (upload)</Label><Input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0])} data-testid="intervenant-photo-upload" /></div>
            )}
            <div><Label>{editId ? 'URL Photo' : 'Ou URL Photo'}</Label><Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://..." data-testid="intervenant-photo-input" /></div>
            <div><Label>Ordre</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} /></div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-[#1E3A5F] hover:bg-[#152A45]" data-testid="intervenant-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editId ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
